import type { Exercise, ExerciseKind } from '@nelnela/contracts';

/**
 * The card session's decisions (M10-iv), kept out of the screen so they can be tested.
 *
 * The web suite is node with no DOM, so a component's behaviour is unreachable to it (M6c's known
 * gap). Everything here is a pure function over the session's state, and `Cards.tsx` is wiring —
 * the same split `lib/journal.ts` and `lib/chat.ts` already make, and for the same reason.
 *
 * THE SUMMARY OWNS THE NO-RE-DEAL RULE, which is a design decision and not an implementation
 * detail. FSRS short-term scheduling is off by design (`fsrs.ts:28`), so a card seen twice in one
 * day collapses into one worst-grade review: re-dealing tonight's misses would teach the model
 * nothing and would teach the LEARNER that misses are free. The session ends when the deck is
 * done, the summary says "tomorrow", and it means it.
 */

/** A card the learner has not finished with yet. */
export interface CardState {
  exercise: Exercise;
  /** flipped is only meaningful for `flash_meaning` — a production card has no back to turn to */
  flipped: boolean;
  /** what the learner did with it, once they have */
  outcome: Outcome | null;
}

export type Outcome =
  | { kind: 'judged'; verdict: 'good' | 'again' }
  | { kind: 'typed'; correct: boolean; expected: string; typed: string }
  /** a phrase card with no resolved item: practised, deliberately unrecorded */
  | { kind: 'practised' };

export interface Session {
  cards: CardState[];
  idx: number;
  startedAt: number;
}

export function initSession(list: readonly Exercise[], now: number): Session {
  return {
    cards: list.map((exercise) => ({ exercise, flipped: false, outcome: null })),
    idx: 0,
    startedAt: now,
  };
}

export const current = (s: Session): CardState | null => s.cards[s.idx] ?? null;
export const isDone = (s: Session): boolean => s.idx >= s.cards.length;

/**
 * The whole card is the flip target (α, binding): 250 ms turn, and swipe was rejected because a
 * grading UI must not hide its gestures. Flipping is idempotent — a second tap on a card already
 * face-up is not a request to turn it back, which would let a learner un-see the answer.
 */
export function flip(s: Session): Session {
  const card = current(s);
  if (!card || card.flipped || card.exercise.kind !== 'flash_meaning') return s;
  return { ...s, cards: s.cards.map((c, i) => (i === s.idx ? { ...c, flipped: true } : c)) };
}

/** Record an outcome and move on. The session never re-deals; see the module comment. */
export function resolve(s: Session, outcome: Outcome): Session {
  if (isDone(s)) return s;
  return {
    ...s,
    cards: s.cards.map((c, i) => (i === s.idx ? { ...c, outcome } : c)),
    idx: s.idx + 1,
  };
}

/**
 * Can this card be graded at all?
 *
 * A phrase card whose saved row was never resolved to an item carries NO item ids, and the server
 * writes one event per tested item — so answering it writes nothing. The learner still practised,
 * and the summary says so; what it must not do is call that a miss or a win, because neither was
 * recorded and claiming otherwise would make the numbers a lie.
 */
export const isGradable = (ex: Exercise): boolean => ex.itemIds.length > 0;

/** `flash_meaning` with an audio front hides its Georgian until the flip — that is what it charges for. */
export const isAudioFront = (ex: Exercise): boolean => ex.prompt.front === 'audio';

/**
 * What the front shows. Null means "there is nothing to read here" — an audio card, whose front is
 * the play control alone.
 */
export function frontText(ex: Exercise): string | null {
  if (ex.kind === 'flash_production') return ex.prompt.text ?? null;
  return isAudioFront(ex) ? null : (ex.prompt.text ?? null);
}

/** What the back shows, for the kind that has one. */
export const backText = (ex: Exercise): string | null => ex.prompt.back ?? null;

/**
 * The Georgian the back leads with, whichever face the card was dealt from.
 *
 * NOT the same as `frontText`, and that difference is the point: an audio card has no front text
 * by design, so a back that read the front showed the learner English alone — a sound with no
 * spelling. The fallback is for exercise rows drafted before `prompt.reveal` existed; on a written
 * card it is the same string, and on an audio one there is nothing better to fall back to.
 */
export const revealText = (ex: Exercise): string | null => ex.prompt.reveal ?? frontText(ex);

export interface Summary {
  cards: number;
  minutes: number;
  /** graded misses — what actually comes back tomorrow */
  notYet: number;
  /** graded wins */
  knewIt: number;
  /** ungradable phrase cards. α, binding: these count as PRACTISED, never as either of the above */
  practised: number;
  /** how many cards each axis saw, from the front each was dealt with */
  perAxis: { read: number; listen: number; produce: number };
  /** the words a miss or a win moved, in the order they were dealt */
  moved: string[];
}

/**
 * The end-of-session facts.
 *
 * `practised` is its own column and not folded into either verdict, which is α's binding call and
 * the honest one: an ungradable phrase card wrote no event, so counting it as a win would inflate
 * a number the engine never saw, and counting it as a miss would promise a return tomorrow that
 * is not coming.
 *
 * The axis a card saw is derived from its FRONT, exactly as the server derives the event's
 * modality (`flashModality`) — an audio card is listening whatever else it is, and a typed
 * production card is producing. Deriving it from the kind would collapse the two faces of
 * `flash_meaning` into one axis and make the summary disagree with the events it is summarising.
 */
export function summarise(s: Session, now: number): Summary {
  const perAxis = { read: 0, listen: 0, produce: 0 };
  let notYet = 0;
  let knewIt = 0;
  let practised = 0;
  const moved: string[] = [];
  for (const c of s.cards) {
    if (!c.outcome) continue;
    perAxis[axisOf(c.exercise)] += 1;
    if (c.outcome.kind === 'practised') {
      practised += 1;
      continue;
    }
    const ok = c.outcome.kind === 'judged' ? c.outcome.verdict === 'good' : c.outcome.correct;
    if (ok) knewIt += 1;
    else notYet += 1;
    const label = frontText(c.exercise) ?? backText(c.exercise);
    if (label) moved.push(label);
  }
  return {
    cards: s.cards.filter((c) => c.outcome).length,
    // Rounded up, and never zero: a session that took forty seconds took "1 minute", because "0
    // minutes" reads as a bug rather than as brevity.
    minutes: Math.max(1, Math.round((now - s.startedAt) / 60_000)),
    notYet,
    knewIt,
    practised,
    perAxis,
    moved,
  };
}

export function axisOf(ex: Exercise): 'read' | 'listen' | 'produce' {
  if (ex.kind === 'flash_production') return 'produce';
  return isAudioFront(ex) ? 'listen' : 'read';
}

/** Which kinds a card session asks for. A SET since M10-iv — the axis chooses within it. */
export const CARD_KINDS: readonly ExerciseKind[] = ['flash_meaning', 'flash_production'];
